import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Collapse } from 'react-bootstrap';
import { BrowserRouter, Route } from 'react-router-dom';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import { Breed } from '../../../../structures/models/breed';
import useScrollToRef from '../../../../utils/hooks/useScrollToRef';
import BreedCarousel from '../../slider/breed-carousel';
import BreedArticles from '../breed-articles';
import { BreedArticleCardInterface } from '../../card/breed-article-card';
import BreedSearch from '../breed-search';
import SimilarBreeds from '../similar-breeds';
import BreedFaq from '../breed-faq';
import { SimilarBreedCardInterface } from '../../card/similar-breed-card';
import BreederGlance from './breed-at-a-glance-main';
import BreedSubHeader from './partials/BreedSubHeader';
import BreedHeading from './partials/BreedHeading';
import BreedHeader from './partials/BreedHeader';

interface BreedPageProps {
  breed: Breed;
  breedArticles: BreedArticleCardInterface[];
  similarBreeds: SimilarBreedCardInterface[];
  states: string[];
  listingType: string;
  listingTypePlural: string;
  cities: string[];
  filteredStates: string[];
  filteredStateFullName: string;
}

const breakpoint = 1024;

const BreedPage: React.FC<BreedPageProps> = ({
  breed,
  breedArticles,
  similarBreeds,
  states,
  listingType,
  listingTypePlural,
  cities,
  filteredStates,
  filteredStateFullName,
}) => {
  const { width } = useWindowDimensions();
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  const learnMoreRef = useRef(null);
  const scrollToLearnMore = useScrollToRef(learnMoreRef);

  const searchComponentRef = useRef(null);
  const scrollToSearch = useScrollToRef(searchComponentRef, 0);

  return (
    <>
      <div
        id="search-component"
        className="filter-section js-redirect-on-autocomplete-submit py-4"
        ref={searchComponentRef}
      >
        <BrowserRouter>
          <Route
            path="/"
            render={(routeProps) => (
              <BreedSearch
                breedName={breed.name}
                listingsType={listingType}
                states={states}
                cities={cities}
                filteredStates={filteredStates}
                filteredStateFullName={filteredStateFullName}
                {...routeProps}
              />
            )}
          />
        </BrowserRouter>
      </div>

      <BreedHeading>
        {`${breed.name} Breed Information`}
      </BreedHeading>

      <BreedHeader
        breed={breed}
        width={width}
        breakpoint={breakpoint}
        listingTypePlural={listingTypePlural}
        isLearnMoreOpen={isLearnMoreOpen}
        setIsLearnMoreOpen={setIsLearnMoreOpen}
        scrollToSearch={scrollToSearch}
      />

      <Collapse
        in={isLearnMoreOpen}
        onEntering={() => scrollToLearnMore()}
      >
        <div className="learn-more-content-container">
          {width < breakpoint && (
            <div className="container mt-3" ref={learnMoreRef}>
              <BreedSubHeader breed={breed} />
            </div>
          )}

          <BreederGlance breedNamePlural={breed.pluralName} breed={breed} />
          <BreedFaq breedsFaqArray={breed.faq} />
          <BreedCarousel breed={breed} />
          <BreedArticles articles={breedArticles} />
        </div>
      </Collapse>

      <SimilarBreeds
        listingType={listingType}
        breeds={similarBreeds}
      />
    </>
  );
};

export default BreedPage;

document.querySelectorAll('.react-breed-page').forEach((element) => {
  const breed = JSON.parse(element.getAttribute('data-breed') ?? '{}');
  const articles = JSON.parse(element.getAttribute('data-articles') ?? '[]');
  const similarBreeds = JSON.parse(element.getAttribute('data-similar-breeds') ?? '[]');
  const states = JSON.parse(element.getAttribute('data-states') ?? '[]');
  const listingType = element.getAttribute('data-listing-type') ?? '';
  const listingTypePlural = element.getAttribute('data-listing-type-plural') ?? '';
  const cities = JSON.parse(element.getAttribute('data-cities') ?? '[]');
  const filteredStates = JSON.parse(element.getAttribute('data-filtered-states') ?? '[]');
  const filteredStateFullName = element.getAttribute('data-filtered-state-full-name') ?? '';

  ReactDOM.render(
    <BreedPage
      breed={breed}
      breedArticles={articles}
      similarBreeds={similarBreeds}
      states={states}
      listingType={listingType}
      listingTypePlural={listingTypePlural}
      cities={cities}
      filteredStates={filteredStates}
      filteredStateFullName={filteredStateFullName}
    />,
    element,
  );
});
